import { Component } from 'react';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { lazyInject } from '../../../context/module';
import { Logger } from '../../../models/logger';
import { ApiConfig } from '../../../services/api-config';
import { LightHttp } from '../../../services/light-http.service';
import { UIContext } from '../../../services/ui-context';
import { OptionalChildrenProps } from '../../../types/react.types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import { Rating } from '../../../utils/request.utils';
import styles from './rating-display.component.module.scss';


interface RatingDisplayComponentProps extends OptionalChildrenProps {
    description: string;
    title: string;
    feature: string;
    rating: Rating;
}

interface RatingDisplayComponentState {
    totalRatings: number;
    currentRating: number;
    currentRatingStr: string;
    jsonSchema;
}


export default class RatingDisplayComponent extends Component<RatingDisplayComponentProps, RatingDisplayComponentState> {
    private readonly logger = Logger.getLogger('RatingDisplayComponent');

    readableFeatureName?: string;

    @lazyInject(LightHttp) private http: LightHttp;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;

    constructor(props) {
        super(props);
        this.state = this.generateState(this.props.rating)
    }

    componentDidMount() {
        if (BrowserUtils.hasWindow()) {
            this.loadRatings$().subscribe({
                error: (err) => {
                    this.logger.error({ error: err, message: `Error getting rating.` });
                    this.setUp(this.props.rating);
                }
            });
        } else {
            this.setUp(this.props.rating);
        }
    }

    private setUp(ratingInfo: Rating) {
        this.setState(this.generateState(ratingInfo));
    }

    private generateState(ratingInfo: Rating) {
        const ratings = this.generateRatings(ratingInfo);
        const script = this.generateRatingScript(ratingInfo, ratings.currentRating, ratings.totalRatings);
        return {
            ...ratings,
            jsonSchema: script,
        }
    }

    private generateRatings(ratingInfo: Rating) {
        this.readableFeatureName = this.props.feature.replace(/-/g, ' ');
        if (ratingInfo) {
            const totalRatings =
                ((ratingInfo.rating_1 || 0) +
                    (ratingInfo.rating_2 || 0) +
                    (ratingInfo.rating_3 || 0) +
                    (ratingInfo.rating_4 || 0) +
                    (ratingInfo.rating_5 || 0)) || 1;
            const currentRatingFloat = ((ratingInfo.rating_1 || 0) + 2 * (ratingInfo.rating_2 || 0) +
                3 * (ratingInfo.rating_3) + 4 * (ratingInfo.rating_4) +
                5 * (ratingInfo.rating_5)) / totalRatings;
            const currentRating = parseFloat(Number(currentRatingFloat).toFixed(1));
            const currentRatingStr = Number(currentRating).toFixed(1);
            return {
                totalRatings,
                currentRating,
                currentRatingStr,
            }
        }
    }

    private generateRatingScript(ratingInfo: Rating, currentRating: number, totalRatings: number) {
        if (ratingInfo) {
            const json = {
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                'name': this.props.title || this.props.feature,
                'description': this.props.description,
                'applicationCategory': 'MultimediaApplication',
                'operatingSystem': 'Any',
                'offers': {
                    '@type': 'Offer',
                    'price': '0',
                    'priceCurrency': 'USD',
                },
                'aggregateRating': {
                    '@type': 'AggregateRating',
                    'worstRating': '1',
                    'bestRating': '5',
                    'ratingValue': currentRating,
                    'ratingCount': totalRatings,
                },
            };
            return JSON.stringify(json);
        }
    }

    rateFeature(rating: number) {
        this.http.put(`${this.apiConfig.getHttpsEndpoint()}/ratings/${this.props.feature}`, {
            value: rating,
        }).subscribe({
            next: (res: any) => {
                if (res.rated) {
                    this.uiContext.successToast('Thank you for rating!');
                    setTimeout(() => {
                        this.loadRatings$().subscribe();
                        this.forceUpdate();
                    }, 500);
                } else {
                    this.uiContext.warnToast('You can only rate a feature once', '', false, false);
                }
            },
            error: err => {
                this.logger.error({ error: err, message: `Error rating feature ${this.props.feature} to ${rating}.` });
            },
        }
        );
    }

    getRatingStars() {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            if (this.state.currentRating >= i) {
                if (this.state.currentRating < i + 1) {
                    stars.push('half');
                } else {
                    stars.push('full');
                }
            } else {
                stars.push('empty');
            }
        }
        return <div className={`${styles['stars']}`}>
            {stars.map((v, i) => {
                return <span key={`star-${i}`}
                    onClick={(e) => this.rateFeature(i + 1)}
                    dangerouslySetInnerHTML={{ __html: v === 'full' ? HtmlEntityTypes.STAR : (v === 'half' ? HtmlEntityTypes.HALF_START : HtmlEntityTypes.NO_STAR) }}>
                </span>
            })
            }
        </div>
    }

    loadRatings$() {
        return this.http.get<Rating>(`${this.apiConfig.getHttpsEndpoint()}/ratings/${this.props.feature}`).pipe(
            tap((data: Rating) => this.setUp(data)),
            catchError(err => {
                this.logger.error({ error: err, message: `Error getting rating.` });
                return of({});
            }),
        );
    }

    render() {
        return <div className="section" >
            <div className={styles['rating-container']}>
                <h4>Rate {this.props.title} Tool</h4>
                <div>
                    {this.getRatingStars()}
                    <span className="text ms-3">Rating: {this.state.currentRatingStr} / 5 - {this.state.totalRatings} reviews</span>
                </div>
            </div >

        </div >
    }
}